import { createApi } from '@reduxjs/toolkit/query/react';

import { apiGocardlessPaymentBatches } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import {
  PaymentBatchCreateRequest,
  PaymentBatchDetail,
  PaymentBatchInvoicesResponse,
  PaymentBatchListResponse,
  PaymentBatchPreviewQueryArgs,
  PaymentBatchPreviewResponse,
} from '~direct-debits/models';

interface BatchDetailQueryArgs {
  batch_id: string;
}

interface BatchClientInvoicesQueryArgs {
  batch_id: string;
  client_id: number;
}

export const paymentBatchesApi = createApi({
  reducerPath: 'directDebitsPaymentBatchesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiGocardlessPaymentBatches }),
  tagTypes: ['payment-batches', 'payment-batch-detail', 'payment-batch-invoices'],
  endpoints: (builder) => ({
    getPaymentBatches: builder.query<PaymentBatchListResponse, Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'payment-batches', id: 'LIST' }],
      transformResponse: (payload) => unwrap<PaymentBatchListResponse>(payload),
    }),
    getPaymentBatchDetail: builder.query<PaymentBatchDetail, BatchDetailQueryArgs>({
      query: ({ batch_id }) => ({ url: `/${batch_id}`, method: 'GET' }),
      providesTags: (_result, _error, { batch_id }) => [{ type: 'payment-batch-detail', id: batch_id }],
      transformResponse: (payload) => unwrap<PaymentBatchDetail>(payload),
    }),
    getPaymentBatchClientInvoices: builder.query<PaymentBatchInvoicesResponse, BatchClientInvoicesQueryArgs>({
      query: ({ batch_id, client_id }) => ({
        url: `/${batch_id}/clients/${client_id}/invoices`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { batch_id, client_id }) => [
        { type: 'payment-batch-invoices', id: `${batch_id}:${client_id}` },
      ],
      transformResponse: (payload) => unwrap<PaymentBatchInvoicesResponse>(payload),
    }),
    // No tag — the preview is a derived view that changes whenever filters or
    // upstream invoices/mandates do. The wizard re-runs it as the user edits the
    // cutoff; caching across instances would surface stale stats.
    //
    // RTK Query caches by endpoint + serialized args regardless of `providesTags`,
    // so omitting the tag isn't enough — the default `keepUnusedDataFor: 60s`
    // would otherwise serve a stale preview if the wizard were closed and
    // reopened within that window. Drop the cache the moment the last
    // subscriber unmounts so the next mount triggers a fresh fetch.
    getPaymentBatchPreview: builder.query<PaymentBatchPreviewResponse, PaymentBatchPreviewQueryArgs | void>({
      query: (params) => ({ url: '/preview', method: 'GET', params: params ?? undefined }),
      keepUnusedDataFor: 0,
      transformResponse: (payload) => unwrap<PaymentBatchPreviewResponse>(payload),
    }),
    createPaymentBatch: builder.mutation<PaymentBatchDetail, PaymentBatchCreateRequest>({
      query: (body) => ({ url: '', method: 'POST', body }),
      // After creation the list view's row count and stats are stale — invalidate
      // the LIST tag so the table re-fetches when the wizard closes.
      invalidatesTags: [{ type: 'payment-batches', id: 'LIST' }],
      transformResponse: (payload) => unwrap<PaymentBatchDetail>(payload),
    }),
  }),
});

export const {
  useGetPaymentBatchesQuery,
  useGetPaymentBatchDetailQuery,
  useGetPaymentBatchClientInvoicesQuery,
  useGetPaymentBatchPreviewQuery,
  useCreatePaymentBatchMutation,
} = paymentBatchesApi;
